// apps/cli/src/lib/prereq-run.ts
//
// Composes the probes (prereq-probes.ts) with the pure evaluators (prereqs.ts).
// Every Azure-touching dependency arrives through `PrereqDeps` so the runner is
// testable with only the OUTERMOST edges mocked — no evaluator is stubbed here,
// which is what stops a passing suite from proving nothing about the wiring.

import type { TokenCredential } from "@azure/identity";
import type { AzAccountInfo } from "./auth.js";
import {
  REQUIRED_PROVIDERS,
  evaluateAppRegistration,
  evaluateAzSignedIn,
  evaluateFoundryAccess,
  evaluateKeyVaultAccess,
  evaluateModelQuota,
  evaluatePlatformDiscoverable,
  evaluateProviders,
  evaluateRedirectUri,
  evaluateSoftDeletedAccounts,
  evaluateSubscriptionAdmin,
  summarize,
  type AccessProbe,
  type PrereqPhase,
  type PrereqResult,
  type PrereqVerdict,
  type ProviderState,
  type QuotaEvaluation,
  type RedirectUriProbe,
  type SoftDeletedEvaluation,
} from "./prereqs.js";

/** Every outermost edge, injected. Defaults are wired in the command, not here. */
export interface PrereqDeps {
  getAzAccount: () => Promise<AzAccountInfo | null>;
  getCallerObjectId: () => Promise<string | null>;
  checkSubScopeAdmin: (oid: string, subscriptionId: string) => Promise<{ ok: boolean; role?: string }>;
  checkAppRegCapability: (oid: string) => Promise<{ ok: boolean; inconclusive: boolean }>;
  probeProviders: (namespaces: readonly string[], subscriptionId?: string) => Promise<ProviderState[]>;
  registerProviders: (namespaces: readonly string[], opts: { subscriptionId?: string; onProgress?: (m: string) => void }) => Promise<void>;
  probeModelQuota: (region: string, model: string) => Promise<QuotaEvaluation>;
  /** Subscription-wide: the region judges the result, it does not scope the read. */
  probeSoftDeletedAccounts: (subscriptionId?: string) => Promise<SoftDeletedEvaluation>;

  discoverPlatform: () => Promise<{
    gatewayUrl: string;
    gatewayClientId: string;
    subscriptionId: string;
    resourceGroup: string;
    projectEndpoint: string | null;
  } | null>;
  resolveFoundryAccountId: (projectEndpoint: string) => Promise<string | null>;
  resolvePlatformKeyVault: (resourceGroup: string, subscriptionId?: string) => Promise<{ uri: string; id: string } | null>;
  resolvePrincipalObjectId: (who: string) => Promise<string | null>;
  probeFoundryAccess: (a: {
    credential: TokenCredential; projectEndpoint: string; accountId: string | null; principalId: string | null; isSelf: boolean;
  }) => Promise<AccessProbe>;
  probeKeyVaultAccess: (a: {
    credential: TokenCredential; kvUri: string | null; kvResourceId: string | null; principalId: string | null; isSelf: boolean;
  }) => Promise<AccessProbe>;
  probeRedirectUri: (clientId: string | null, gatewayUrl: string | null) => Promise<RedirectUriProbe>;

  fixFoundryAccess: (a: { credential: TokenCredential; subscriptionId: string; accountId: string; principalId: string }) => Promise<void>;
  fixKeyVaultAccess: (a: { credential: TokenCredential; subscriptionId: string; kvResourceId: string; principalId: string }) => Promise<void>;

  credential: () => TokenCredential;
  /** True only for "this subscription holds no m8t deployment". */
  isNoPlatformError: (e: unknown) => boolean;
}

export interface PrereqRunOptions {
  fix: boolean;
  /** UPN or object id of someone else. Usage phase only. */
  forPrincipal?: string;
  /** Region + model the install will target — install phase only. */
  region?: string;
  model?: string;
  /** Supplied --client-id short-circuits the directory-admin check. */
  clientId?: string;
  subscription?: string;
  resourceGroup?: string;
  onProgress?: (m: string) => void;
}

/**
 * Which phase can be answered right now.
 *
 * Only a genuine "no deployment in this subscription" selects the install phase.
 * An ambiguous or failed discovery is deliberately allowed to throw: reporting
 * the install phase to someone who already has a platform is a confidently wrong
 * answer, and the discovery error carries the fix (usually --resource-group).
 */
export async function detectPhase(deps: PrereqDeps): Promise<PrereqPhase> {
  try {
    return (await deps.discoverPlatform()) ? "usage" : "install";
  } catch (e) {
    if (deps.isNoPlatformError(e)) return "install";
    throw e;
  }
}

export async function runInstallPhase(deps: PrereqDeps, opts: PrereqRunOptions): Promise<PrereqVerdict> {
  const results: PrereqResult[] = [];
  const account = await deps.getAzAccount().catch(() => null);
  results.push(evaluateAzSignedIn(account));
  if (!account) return summarize("install", results);

  const subscriptionId = opts.subscription ?? account.subscriptionId;
  const oid = await deps.getCallerObjectId().catch(() => null);

  if (oid) {
    results.push(evaluateSubscriptionAdmin(await deps.checkSubScopeAdmin(oid, subscriptionId).catch(() => ({ ok: false }))));
    results.push(
      opts.clientId
        ? evaluateAppRegistration({ ok: true, inconclusive: false }, opts.clientId)
        : evaluateAppRegistration(await deps.checkAppRegCapability(oid).catch(() => ({ ok: false, inconclusive: true }))),
    );
  }

  // Providers: register FIRST when fixing, then re-probe. The verdict always comes
  // from a fresh read — never from "we asked Azure to register it", which is not
  // the same statement.
  if (opts.fix) {
    const before = await deps.probeProviders(REQUIRED_PROVIDERS, subscriptionId);
    const missing = before
      .filter((s) => s.state !== null && s.state.toLowerCase() !== "registered")
      .map((s) => s.namespace);
    if (missing.length > 0) {
      opts.onProgress?.(`registering ${missing.length.toString()} resource provider(s)…`);
      await deps.registerProviders(missing, { subscriptionId, onProgress: opts.onProgress });
    }
  }
  results.push(evaluateProviders(await deps.probeProviders(REQUIRED_PROVIDERS, subscriptionId)));

  // Soft-deleted accounts, read BEFORE quota and reported above it: a
  // soft-deleted Cognitive Services account keeps its model quota until it is
  // purged, so the quota row below can come back perfectly clear while the
  // capacity is already spoken for. The founder sees the reason before the row
  // that looks fine.
  //
  // The probe reports its own failures rather than throwing; this catch is the
  // belt to that braces, and caps the message for the same reason the probe does
  // — an az error can run to kilobytes, and this lands in a rendered row.
  const softDeleted = await deps.probeSoftDeletedAccounts(subscriptionId).catch((e: unknown): SoftDeletedEvaluation => {
    const raw = e instanceof Error ? e.message : String(e);
    return { readable: false, accounts: [], reason: raw.length > 300 ? `${raw.slice(0, 297)}…` : raw };
  });
  results.push(evaluateSoftDeletedAccounts({ ...softDeleted, ...(opts.region ? { region: opts.region } : {}) }));

  if (opts.region && opts.model) {
    results.push(evaluateModelQuota(await deps.probeModelQuota(opts.region, opts.model)));
  }

  return summarize("install", results);
}

export async function runUsagePhase(deps: PrereqDeps, opts: PrereqRunOptions): Promise<PrereqVerdict> {
  const results: PrereqResult[] = [];
  // Same distinction as detectPhase: a genuine absence is a reportable row; an
  // ambiguous discovery throws so the caller sees which platform to name.
  let platform: Awaited<ReturnType<PrereqDeps["discoverPlatform"]>> = null;
  try {
    platform = await deps.discoverPlatform();
  } catch (e) {
    if (!deps.isNoPlatformError(e)) throw e;
  }
  results.push(
    evaluatePlatformDiscoverable({
      gatewayUrl: platform?.gatewayUrl ?? null,
      detail: platform ? "" : "no m8t deployment found for this Azure session",
    }),
  );
  if (!platform) return summarize("usage", results);

  const credential = deps.credential();
  const selfOid = await deps.getCallerObjectId().catch(() => null);
  const isSelf = !opts.forPrincipal;
  const principalId = isSelf ? selfOid : await deps.resolvePrincipalObjectId(opts.forPrincipal ?? "").catch(() => null);
  if (!isSelf && !principalId) {
    // Asked to set a NAMED person up and we could not find them. Reporting
    // "skipped" would exit 0 and leave the admin believing it worked.
    throw new UnknownPrincipalError(opts.forPrincipal ?? "");
  }

  // Redirect URI — REPORTED, never written, and self only.
  //
  // This is a property of the DEPLOYMENT, not of the person running the command,
  // and the object it lives on is an Entra app registration that the installer
  // reuses across every deployment in the tenant — production included. `--fix`
  // writing it meant anyone running this command, on any machine, patched a
  // shared directory object; and because the write is read-modify-write rather
  // than atomic, two concurrent installs could drop each other's entry.
  //
  // Registering it belongs to the install, which knows it is the installer. Here
  // we only tell the caller it is missing, and what to run.
  if (isSelf) {
    results.push(evaluateRedirectUri(await deps.probeRedirectUri(platform.gatewayClientId, platform.gatewayUrl)));
  }

  const accountId = platform.projectEndpoint ? await deps.resolveFoundryAccountId(platform.projectEndpoint).catch(() => null) : null;
  const kv = await deps.resolvePlatformKeyVault(platform.resourceGroup, platform.subscriptionId).catch(() => null);

  const foundryProbe = async (): Promise<AccessProbe | null> =>
    platform.projectEndpoint
      ? deps.probeFoundryAccess({ credential, projectEndpoint: platform.projectEndpoint, accountId, principalId, isSelf })
      : null;

  // A missing project endpoint means we cannot judge Foundry access. Emitting
  // NOTHING would let the verdict read as "everything checked out" while the
  // single most important row was silently absent.
  if (!platform.projectEndpoint) {
    results.push(
      evaluateFoundryAccess({ evidence: "unavailable", scope: null, principalId, isSelf }),
    );
  }
  let foundry: AccessProbe | null = await foundryProbe();
  if (opts.fix && foundry && needsGrant(foundry) && accountId && principalId) {
    opts.onProgress?.("granting Foundry data-plane access…");
    await deps.fixFoundryAccess({ credential, subscriptionId: platform.subscriptionId, accountId, principalId });
    // Deliberately NOT re-probed here: a fresh data-plane grant takes ~20s to
    // become visible, so an immediate re-probe reports a false 403. The grant is
    // reported as applied; the next `m8t prereqs` run reads the settled truth.
    results.push({ ...evaluateFoundryAccess(foundry), status: "fixed", detail: "granted Foundry User — allow up to a minute to take effect" });
    foundry = null;
  }
  if (foundry) results.push(evaluateFoundryAccess(foundry));

  let keyvault: AccessProbe | null = await deps.probeKeyVaultAccess({
    credential, kvUri: kv?.uri ?? null, kvResourceId: kv?.id ?? null, principalId, isSelf,
  });
  if (opts.fix && needsGrant(keyvault) && kv && principalId) {
    opts.onProgress?.("granting Key Vault secrets access…");
    await deps.fixKeyVaultAccess({ credential, subscriptionId: platform.subscriptionId, kvResourceId: kv.id, principalId });
    results.push({ ...evaluateKeyVaultAccess(keyvault), status: "fixed", detail: "granted Key Vault Secrets User — allow up to a minute to take effect" });
    keyvault = null;
  }
  if (keyvault) results.push(evaluateKeyVaultAccess(keyvault));

  return summarize("usage", results);
}

/** Raised when `--for <who>` names someone the directory cannot resolve. */
export class UnknownPrincipalError extends Error {
  constructor(readonly who: string) {
    super(
      `Could not find '${who}' in this directory. ` +
        `Pass the exact user principal name (e.g. alice@contoso.com) or their object id.`,
    );
    this.name = "UnknownPrincipalError";
  }
}

/** A grant is warranted on a real denial or on a confirmed missing assignment — never on an unavailable probe. */
function needsGrant(p: AccessProbe): boolean {
  if (p.evidence === "probe") return p.status === 401 || p.status === 403;
  if (p.evidence === "assignment") return p.granted === false;
  return false;
}

export async function runPrereqs(deps: PrereqDeps, opts: PrereqRunOptions & { phase?: PrereqPhase }): Promise<PrereqVerdict> {
  const phase = opts.phase ?? (await detectPhase(deps));
  return phase === "install" ? runInstallPhase(deps, opts) : runUsagePhase(deps, opts);
}
